// Public-register connectors: official data fetched from public registers on a schedule or on demand, written as
// entities and claims through the claims service with the register's own page as evidence. The European
// Parliament connector reads roll-call votes (HowTheyVote.eu open data), the meetings MEPs declare with interest
// representatives, written questions with every co-signer, and MEPs' posts in committees and delegations.
// Fetched text is data, never instructions; nothing fetched reaches a model.
import type { StageDef } from './progress';
import { SCRAPER_SCHEDULES, type ScraperSchedule } from './scrapers';

export const REGISTER_KINDS = ['european_parliament', 'lobby_funding'] as const;
export type RegisterKind = (typeof REGISTER_KINDS)[number];
export const REGISTER_KIND_LABELS: Record<RegisterKind, string> = {
  european_parliament: 'European Parliament',
  lobby_funding: 'Lobby and funding registers',
};

/** Connectors run on the website monitors' schedules (checked by the same half-hourly cron pass). */
export const REGISTER_SCHEDULES = SCRAPER_SCHEDULES;
export type RegisterSchedule = ScraperSchedule;

export const EP_DATASETS = ['votes', 'meetings', 'questions', 'posts'] as const;
export type EpDataset = (typeof EP_DATASETS)[number];
export const EP_DATASET_LABELS: Record<EpDataset, string> = {
  votes: 'Roll-call votes (HowTheyVote.eu)',
  meetings: 'Declared meetings with interest representatives',
  questions: 'Written questions and their co-signers',
  posts: 'Posts in committees and delegations',
};

export interface EpMep {
  /** The Parliament's MEP id (also HowTheyVote's member id), e.g. `197408`. */
  id: string;
  /** As the Parliament writes it, e.g. `Hildegard BENTELE`. */
  name: string;
}

export interface EpSettings {
  datasets: EpDataset[];
  /** The MEPs to fetch. Empty: every MEP (votes then cover every member who voted; meetings need keywords). */
  meps: EpMep[];
  /** ISO dates (YYYY-MM-DD), inclusive; null: open. */
  dateFrom: string | null;
  dateTo: string | null;
  /** HowTheyVote vote ids, e.g. `198051`. */
  voteIds: string[];
  /** A HowTheyVote search (words in the title) for main votes in the date range, used when no ids are given. */
  voteSearch: string | null;
  /** Words searched in a meeting's title and attendees, one query each (several languages: Israel, Israël, Izrael…). */
  meetingKeywords: string[];
  /** Keep a meeting only when its title or attendees also contain one of these (e.g. embassy, mission, ambassador). */
  meetingAlsoMentions: string[];
  /** Written questions by reference, e.g. `E-000932/2022`. With MEPs and dates, their questions are found too. */
  questionRefs: string[];
}

// ── Lobby and funding registers ──────────────────────────────────────────────────────────────────────────

export const LOBBY_DATASETS = ['transparency', 'commission_meetings', 'fara', 'nonprofits', 'eu_grants'] as const;
export type LobbyDataset = (typeof LOBBY_DATASETS)[number];
export const LOBBY_DATASET_LABELS: Record<LobbyDataset, string> = {
  transparency: 'EU Transparency Register: registration, lobbying costs, EU grants',
  commission_meetings: 'Meetings with Commissioners, cabinets and Directors-General',
  fara: 'FARA: US foreign-agent registrations and their foreign principals',
  nonprofits: 'US nonprofit tax filings (990): revenue and grants paid',
  eu_grants: 'EU grants (Funding & Tenders portal)',
};

export interface LobbySettings {
  datasets: LobbyDataset[];
  /** EU Transparency Register ids, e.g. `36101356444-26` (the European Jewish Congress). */
  trIds: string[];
  /** FARA registration numbers, e.g. `7394`. */
  faraRegistrants: string[];
  /** US employer identification numbers of nonprofits, e.g. `13-5563393`. */
  eins: string[];
  /** EU participant identification codes (PIC, 9 digits) of grant beneficiaries, e.g. `881967942`. */
  euPics: string[];
  dateFrom: string | null;
  dateTo: string | null;
}

export type RegisterSettings = EpSettings | LobbySettings;
export const isLobbySettings = (s: RegisterSettings): s is LobbySettings => 'trIds' in s;
export const isEpSettings = (s: RegisterSettings): s is EpSettings => 'meps' in s;

const TR_ID = /^\d{6,15}-\d{2}$/;
/** `13-5563393`, `135563393` → `135563393`; null when it is not an EIN. */
export const einOf = (raw: string) => {
  const d = raw.replace(/[\s-]/g, '');
  return /^\d{9}$/.test(d) ? d : null;
};
/** `135563393` → `13-5563393`. */
export const formatEin = (ein: string) => `${ein.slice(0, 2)}-${ein.slice(2)}`;

/** Plain-language problems with a lobby connector's lists (empty when fine). */
export function validateLobbySettings(s: Partial<LobbySettings>): string[] {
  const errors: string[] = [];
  if (s.datasets !== undefined && (!Array.isArray(s.datasets) || !s.datasets.length || s.datasets.some(d => !LOBBY_DATASETS.includes(d))))
    errors.push('Tick at least one register to read.');
  const bad = (list: unknown, ok: (v: string) => boolean) =>
    Array.isArray(list) ? list.find(v => typeof v !== 'string' || !ok(v.trim())) : list === undefined ? undefined : '';
  const tr = bad(s.trIds, v => TR_ID.test(v));
  if (tr !== undefined) errors.push(`Transparency Register ids look like 36101356444-26${tr ? ` (not “${tr}”)` : ''}.`);
  const fara = bad(s.faraRegistrants, v => /^\d{1,6}$/.test(v));
  if (fara !== undefined) errors.push(`FARA registration numbers are numbers, e.g. 7394${fara ? ` (not “${fara}”)` : ''}.`);
  const ein = bad(s.eins, v => !!einOf(v));
  if (ein !== undefined) errors.push(`EINs have 9 digits, e.g. 13-5563393${ein ? ` (not “${ein}”)` : ''}.`);
  const pic = bad(s.euPics, v => /^\d{9}$/.test(v));
  if (pic !== undefined) errors.push(`A PIC has 9 digits, e.g. 881967942${pic ? ` (not “${pic}”)` : ''}.`);
  for (const [label, v] of [
    ['start', s.dateFrom],
    ['end', s.dateTo],
  ] as const)
    if (v != null && v !== '' && !isIsoDate(v)) errors.push(`The ${label} date must be a date such as 2024-07-01.`);
  if (s.dateFrom && s.dateTo && s.dateFrom > s.dateTo) errors.push('The start date is after the end date.');
  return errors;
}

export function normalizeLobbySettings(s: Partial<LobbySettings> | undefined): LobbySettings {
  const list = (v: unknown, max = 200) => words(v, max, 20);
  return {
    datasets: Array.isArray(s?.datasets) ? LOBBY_DATASETS.filter(d => s.datasets?.includes(d)) : [...LOBBY_DATASETS],
    trIds: list(s?.trIds),
    faraRegistrants: list(s?.faraRegistrants),
    eins: [...new Set(list(s?.eins).map(e => einOf(e) ?? e))],
    euPics: list(s?.euPics),
    dateFrom: s?.dateFrom || null,
    dateTo: s?.dateTo || null,
  };
}

/** What each ticked register needs before a run can fetch anything. */
export function missingLobby(s: LobbySettings): string[] {
  const out: string[] = [];
  if ((s.datasets.includes('transparency') || s.datasets.includes('commission_meetings')) && !s.trIds.length)
    out.push('For the Transparency Register and Commission meetings, give the organizations’ register ids.');
  if (s.datasets.includes('fara') && !s.faraRegistrants.length) out.push('For FARA, give registration numbers.');
  if (s.datasets.includes('nonprofits') && !s.eins.length) out.push('For US nonprofits, give EINs.');
  if (s.datasets.includes('eu_grants') && !s.euPics.length) out.push('For EU grants, give the beneficiaries’ PICs.');
  return out;
}

export function describeLobbySettings(s: LobbySettings): string {
  const parts = [
    s.trIds.length ? `${s.trIds.length} register id${s.trIds.length === 1 ? '' : 's'}` : null,
    s.faraRegistrants.length ? `${s.faraRegistrants.length} FARA registrant${s.faraRegistrants.length === 1 ? '' : 's'}` : null,
    s.eins.length ? `${s.eins.length} EIN${s.eins.length === 1 ? '' : 's'}` : null,
    s.euPics.length ? `${s.euPics.length} PIC${s.euPics.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  const when =
    s.dateFrom && s.dateTo ? `from ${s.dateFrom} to ${s.dateTo}` : s.dateFrom ? `since ${s.dateFrom}` : s.dateTo ? `until ${s.dateTo}` : '';
  return [`${s.datasets.length} register${s.datasets.length === 1 ? '' : 's'} for ${parts.join(', ') || 'nothing yet'}`, when]
    .filter(Boolean)
    .join(' ');
}

export const describeSettings = (s: RegisterSettings) => (isLobbySettings(s) ? describeLobbySettings(s) : describeEpSettings(s));

export const REGISTER_LIMITS = {
  meps: 800,
  voteIds: 100,
  /** Votes a search may add per run. */
  searchVotes: 50,
  keywords: 20,
  questionRefs: 200,
  /** Rows one meetings export answers; a window that fills it is split in two. */
  meetingsExportRows: 1000,
  nameChars: 80,
  issues: 100,
  /** Consecutive failed runs after which a connector pauses itself. */
  pauseAfterFailures: 5,
} as const;

/**
 * Below the matching threshold (Admin → Matching), a person whose best candidate is at least this certain is held
 * for an admin to link or create, never created as a second entity; below it, they are created.
 */
export const REGISTER_HOLD_FLOOR = 0.75;
/** …or whose name is at least this similar (resolution's name score: one-letter spelling differences). */
export const REGISTER_NAME_HOLD = 0.85;

export const REGISTER_RUN_STAGES: StageDef[] = [
  { key: 'fetching', label: 'Fetching from the register', stallAfterMs: 120_000 },
  { key: 'writing', label: 'Writing entities and claims', stallAfterMs: 120_000 },
  { key: 'done', label: 'Done', stallAfterMs: Number.POSITIVE_INFINITY },
];

export type RegisterRunStatus = 'queued' | 'running' | 'ok' | 'partial' | 'failed';
export type RegisterConnectorStatus = 'active' | 'paused' | 'failing';

export interface RegisterRunStats {
  /** Records read from the register (votes × members, meetings, questions, posts). */
  fetched: number;
  /** Records already imported by an earlier run (nothing written again). */
  known: number;
  claims: number;
  entitiesCreated: number;
  entitiesMatched: number;
  /** People or bodies waiting for an admin to say who they are (their records are skipped until then). */
  toReview: number;
  failed: number;
  /** Per dataset: records read. */
  byDataset: Partial<Record<EpDataset | LobbyDataset, number>>;
}

export const emptyRunStats = (): RegisterRunStats => ({
  fetched: 0,
  known: 0,
  claims: 0,
  entitiesCreated: 0,
  entitiesMatched: 0,
  toReview: 0,
  failed: 0,
  byDataset: {},
});

export interface RegisterRunSummary {
  id: string;
  trigger: 'schedule' | 'manual';
  status: RegisterRunStatus;
  stats: RegisterRunStats;
  issues: string[];
  startedAt: number;
  finishedAt: number | null;
  error: string | null;
}

export interface RegisterConnectorSummary {
  id: string;
  kind: RegisterKind;
  name: string;
  settings: RegisterSettings;
  schedule: RegisterSchedule;
  status: RegisterConnectorStatus;
  enabled: boolean;
  owner: { id: string; name: string | null; email: string };
  nextRunAt: number | null;
  lastRunAt: number | null;
  lastStatus: RegisterRunStatus | null;
  lastError: string | null;
  running: boolean;
  /** People or bodies of this connector waiting for a decision. */
  pendingMatches: number;
  createdAt: number;
}

/** A register id (an MEP, a committee) whose entity could not be decided by an exact name: an admin decides. */
export interface PendingMatch {
  register: string;
  externalId: string;
  label: string;
  candidates: { entityId: string; displayName: string; typeKey: string; score: number; matchedName: string }[];
  createdAt: number;
}

export interface RegisterConnectorDetail {
  connector: RegisterConnectorSummary;
  runs: RegisterRunSummary[];
  pending: PendingMatch[];
}

export interface RegisterConnectorInput {
  name?: string;
  kind?: RegisterKind;
  schedule?: RegisterSchedule;
  /** MEPs may be given by EP id or by name (`Hildegard Bentele`); names are looked up in the Parliament's list. */
  settings?: Partial<Omit<EpSettings, 'meps' | 'datasets'>> &
    Partial<Omit<LobbySettings, 'datasets'>> & { meps?: (EpMep | string)[]; datasets?: (EpDataset | LobbyDataset)[] };
}

/** Queue message: one run (or the next slice of a long run) of a connector, run by the pipeline Worker. */
export interface RegisterRunMessage {
  kind: 'register_run';
  runId: string;
}
export const isRegisterRunMessage = (m: unknown): m is RegisterRunMessage =>
  !!m && typeof m === 'object' && (m as RegisterRunMessage).kind === 'register_run' && typeof (m as RegisterRunMessage).runId === 'string';

// ── Validation ───────────────────────────────────────────────────────────────────────────────────────────

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isIsoDate = (s: string) => ISO_DATE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));

/** `E-000932/2022`, `e-932/2022`, `E-9-2022-000932` → `E-000932/2022`; null when it is not a question reference. */
export function questionRef(raw: string): string | null {
  const s = raw.trim().toUpperCase();
  let m = /^([EOPH])-?(\d{1,6})\/(\d{4})$/.exec(s);
  if (m) return `${m[1]}-${(m[2] ?? '').padStart(6, '0')}/${m[3]}`;
  m = /^([EOPH])-\d{1,2}-(\d{4})-(\d{1,6})$/.exec(s);
  if (m) return `${m[1]}-${(m[3] ?? '').padStart(6, '0')}/${m[2]}`;
  return null;
}

/** The parliamentary term a date falls in (9th from 2 July 2019, 10th from 16 July 2024). */
export function epTermOn(isoDate: string): number {
  if (isoDate >= '2024-07-16') return 10;
  if (isoDate >= '2019-07-02') return 9;
  if (isoDate >= '2014-07-01') return 8;
  return 7;
}

/** `E-000932/2022` (asked 2022-03-08) → the Open Data id `E-9-2022-000932`. */
export function questionDocId(ref: string, term: number): string {
  const m = /^([EOPH])-(\d{6})\/(\d{4})$/.exec(ref);
  return m ? `${m[1]}-${term}-${m[3]}-${m[2]}` : ref;
}

const words = (list: unknown, max: number, chars = 60): string[] =>
  Array.isArray(list)
    ? [...new Set(list.filter((w): w is string => typeof w === 'string').map(w => w.trim().slice(0, chars)))].filter(Boolean).slice(0, max)
    : [];

/** Plain-language problems with a connector's settings (empty when fine). MEPs are resolved separately. */
export function validateRegisterInput(input: RegisterConnectorInput, creating: boolean): string[] {
  const errors: string[] = [];
  if (creating || input.name !== undefined) {
    const name = (input.name ?? '').trim();
    if (!name) errors.push('Give the connector a name, e.g. “Pilot list MEPs”.');
    else if (name.length > REGISTER_LIMITS.nameChars) errors.push(`Keep the name under ${REGISTER_LIMITS.nameChars} characters.`);
  }
  if (creating && !REGISTER_KINDS.includes(input.kind as RegisterKind)) errors.push('Choose which register to read.');
  if (input.schedule !== undefined && !REGISTER_SCHEDULES.includes(input.schedule)) errors.push('Choose how often to fetch.');
  const s = input.settings;
  if (creating && !s) errors.push('Choose what to fetch.');
  if (!s) return errors;
  if (input.kind === 'lobby_funding') return [...errors, ...validateLobbySettings(s as Partial<LobbySettings>)];
  if (s.datasets !== undefined) {
    if (!Array.isArray(s.datasets) || !s.datasets.length || s.datasets.some(d => !EP_DATASETS.includes(d as EpDataset)))
      errors.push('Tick at least one kind of record to fetch.');
  }
  for (const [label, v] of [
    ['start', s.dateFrom],
    ['end', s.dateTo],
  ] as const)
    if (v != null && v !== '' && !isIsoDate(v)) errors.push(`The ${label} date must be a date such as 2024-07-01.`);
  if (s.dateFrom && s.dateTo && s.dateFrom > s.dateTo) errors.push('The start date is after the end date.');
  if (s.voteIds !== undefined && (!Array.isArray(s.voteIds) || s.voteIds.some(v => !/^\d{1,9}$/.test(String(v).trim()))))
    errors.push('Vote ids are the numbers in a HowTheyVote address, e.g. 198051 in howtheyvote.eu/votes/198051.');
  if (Array.isArray(s.voteIds) && s.voteIds.length > REGISTER_LIMITS.voteIds)
    errors.push(`Choose at most ${REGISTER_LIMITS.voteIds} votes.`);
  if (s.questionRefs !== undefined) {
    const bad = (Array.isArray(s.questionRefs) ? s.questionRefs : []).filter(r => typeof r !== 'string' || !questionRef(r));
    if (!Array.isArray(s.questionRefs) || bad.length)
      errors.push(`Write question references like E-000932/2022${bad.length ? ` (not “${String(bad[0])}”)` : ''}.`);
    else if (s.questionRefs.length > REGISTER_LIMITS.questionRefs) errors.push(`Give at most ${REGISTER_LIMITS.questionRefs} questions.`);
  }
  if (Array.isArray(s.meps) && s.meps.length > REGISTER_LIMITS.meps) errors.push(`Choose at most ${REGISTER_LIMITS.meps} MEPs.`);
  if (Array.isArray(s.meetingKeywords) && s.meetingKeywords.length > REGISTER_LIMITS.keywords)
    errors.push(`Use at most ${REGISTER_LIMITS.keywords} search words.`);
  return errors;
}

/** Settings as stored: known keys only, lists trimmed and de-duplicated, defaults filled. */
export function normalizeEpSettings(s: Partial<EpSettings> | undefined, meps: EpMep[]): EpSettings {
  const datasets = Array.isArray(s?.datasets) ? EP_DATASETS.filter(d => s.datasets?.includes(d)) : [...EP_DATASETS];
  return {
    datasets,
    meps,
    dateFrom: s?.dateFrom || null,
    dateTo: s?.dateTo || null,
    voteIds: words(s?.voteIds, REGISTER_LIMITS.voteIds, 9),
    voteSearch: typeof s?.voteSearch === 'string' && s.voteSearch.trim() ? s.voteSearch.trim().slice(0, 120) : null,
    meetingKeywords: words(s?.meetingKeywords, REGISTER_LIMITS.keywords),
    meetingAlsoMentions: words(s?.meetingAlsoMentions, REGISTER_LIMITS.keywords),
    questionRefs: [...new Set(words(s?.questionRefs, REGISTER_LIMITS.questionRefs, 20).map(r => questionRef(r) ?? r))],
  };
}

/** What a connector would fetch, in a sentence, for its card. */
export function describeEpSettings(s: EpSettings): string {
  const who = s.meps.length ? `${s.meps.length} MEP${s.meps.length === 1 ? '' : 's'}` : 'every MEP';
  const when =
    s.dateFrom && s.dateTo ? `from ${s.dateFrom} to ${s.dateTo}` : s.dateFrom ? `since ${s.dateFrom}` : s.dateTo ? `until ${s.dateTo}` : '';
  const what = s.datasets.map(d => EP_DATASET_LABELS[d].split(' (')[0]?.toLowerCase()).join(', ');
  return [`${what[0]?.toUpperCase() ?? ''}${what.slice(1)} for ${who}`, when].filter(Boolean).join(' ');
}

/** Folds a word for "mentions" checks: case, accents and ß ignored (`Israël` mentions `israel`). */
export const foldWord = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').toLowerCase();

/** Whether a text contains any of the words (folded; a word matches inside longer words: `embass` → `Embassy`). */
export const mentionsAny = (text: string, list: string[]) => {
  if (!list.length) return true;
  const t = foldWord(text);
  return list.some(w => t.includes(foldWord(w)));
};
